import { Clock, Loader2, Smartphone } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { cn } from '@/lib/utils';
import api from '@/lib/api';
import { FlowHistoryPanel } from '@/components/creationFlows/FlowHistoryPanel';
import { formatNarrationDate } from '@/lib/narration/narrationHistory';

const HISTORY_CACHE_KEY = 'twinnee_reels_shorts_history_v1';
let memoryHistory = [];

function readHistoryCache() {
  if (memoryHistory.length) return memoryHistory;
  try {
    const raw = sessionStorage.getItem(HISTORY_CACHE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    const projects = Array.isArray(parsed?.projects) ? parsed.projects : [];
    memoryHistory = projects;
    return projects;
  } catch {
    return [];
  }
}

function writeHistoryCache(projects) {
  memoryHistory = Array.isArray(projects) ? projects : [];
  try {
    sessionStorage.setItem(
      HISTORY_CACHE_KEY,
      JSON.stringify({ projects: memoryHistory, savedAt: Date.now() }),
    );
  } catch {
    /* quota / private mode */
  }
}

function statusLabel(project) {
  const st = String(project?.status || '').toLowerCase();
  if (st === 'completed' && project?.has_final_video) return 'Video ready';
  if (st === 'completed') return 'Cut ready';
  if (st === 'awaiting_review') return 'Review transcripts';
  if (st === 'running' || st === 'pending') return 'Processing';
  if (st === 'failed') return 'Failed';
  return st || 'Draft';
}

export function ReelsShortsHistoryPanel({
  open,
  onToggle,
  activeProjectId = null,
  onSelectProject,
  onNewProject,
  refreshKey = 0,
  loadingProjectId = null,
}) {
  const [projects, setProjects] = useState(() => readHistoryCache());
  const [loading, setLoading] = useState(() => readHistoryCache().length === 0);
  const [refreshing, setRefreshing] = useState(false);

  const load = useCallback(async () => {
    const hasRows = readHistoryCache().length > 0;
    if (!hasRows) setLoading(true);
    else setRefreshing(true);
    try {
      const { data } = await api.get('/reels-shorts/projects', { timeout: 20_000 });
      const next = Array.isArray(data?.projects) ? data.projects : [];
      writeHistoryCache(next);
      setProjects(next);
    } catch {
      if (!hasRows) setProjects([]);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    if (open) load();
  }, [open, load, refreshKey]);

  return (
    <FlowHistoryPanel
      open={open}
      onToggle={onToggle}
      title="REEL&SHORTS history"
      description="Past snippet reels. Open one to resume transcripts or the AI editor cut."
      icon={Smartphone}
      variant="dark"
      loading={loading && projects.length === 0}
      count={projects.length}
      onRefresh={load}
      refreshLoading={refreshing || (loading && projects.length === 0)}
      onNew={onNewProject}
      newLabel="New reel"
      emptyMessage="No REEL&SHORTS projects yet. Upload snippets to create one."
      collapsedLabel="History"
      testId="reels-shorts-history-panel"
      footer={`${projects.length} project${projects.length === 1 ? '' : 's'}`}
    >
      <ul className="space-y-1">
        {projects.map((p) => {
          const active = p.id === activeProjectId || p.id === loadingProjectId;
          const status = statusLabel(p);
          return (
            <li key={p.id}>
              <button
                type="button"
                onClick={() => onSelectProject(p)}
                disabled={loadingProjectId === p.id}
                className={cn(
                  'w-full text-left rounded-sm px-2.5 py-2 border transition-colors disabled:opacity-60',
                  active
                    ? 'bg-rose-500/15 border-rose-500/40'
                    : 'border-transparent hover:bg-white/5 hover:border-slate-800',
                )}
              >
                <div className="flex items-center justify-between gap-2">
                  <span className="text-sm font-medium text-slate-100 truncate">{p.title}</span>
                  {loadingProjectId === p.id ? (
                    <Loader2 className="w-3.5 h-3.5 animate-spin text-rose-300 shrink-0" />
                  ) : null}
                </div>
                <div className="mt-0.5 flex items-center gap-2 text-[10px] text-slate-500">
                  <span>{status}</span>
                  <span>·</span>
                  <span>{p.snippet_count || 0} clips</span>
                  <span className="inline-flex items-center gap-0.5 ml-auto">
                    <Clock className="w-3 h-3" />
                    {formatNarrationDate(p.updated_at || p.created_at)}
                  </span>
                </div>
              </button>
            </li>
          );
        })}
      </ul>
    </FlowHistoryPanel>
  );
}
